import React, { type ReactNode } from "react"
import { graphql } from "gatsby"
import { ButtonParser } from "components/button/ButtonParser"
import { Content } from "components/content/Content"
import { Image } from "components/image/Image"
import type { StrapiImageItem } from "_types/AssetsImage"
import type { ButtonElements } from "_types/Button"

import "./content-block.module.scss"

type ContentBlockType =
  | "default"
  | "text-wraps-image"
  | "fifty-fifty-grid"
  | "one-third-two-thirds-grid"
  | "one-eigth-seven-eighths-grid"

export interface TextAndImageContentBlockProps {
  alternate_background_color?: boolean
  buttons?: ButtonElements[]
  children?: ReactNode
  content_block_type?: ContentBlockType
  eyebrow_text: string | null
  hasBlockPadding?: boolean
  hasContainerClassName?: boolean
  heading: string | null
  image?: StrapiImageItem
  index?: number
  isAltBlock?: boolean
  sub_heading?: string
  alternateBlocksCount: number
  text: string | null
}

export const TextAndImageContentBlock = ({
  alternate_background_color = false,
  buttons,
  children,
  content_block_type,
  eyebrow_text,
  hasBlockPadding = true,
  hasContainerClassName = true,
  heading,
  image,
  isAltBlock = false,
  sub_heading,
  alternateBlocksCount,
  text,
}: TextAndImageContentBlockProps) => {
  const isContentEmpty = text === "" ? true : false
  const isSubHeadingEmpty = !sub_heading || sub_heading === null

  let blockType =
    content_block_type === "default" ? "text-wraps-image" : content_block_type

  let oddOrEven = ""

  if (isAltBlock) {
    oddOrEven = alternateBlocksCount % 2 ? "even" : "odd"
  }

  const alternatingClassName = isAltBlock
    ? ` ${blockType}--${oddOrEven}`
    : `${blockType}--odd`
  const contentBlockPaddingClassName = hasBlockPadding
    ? ` u-content-block-padding`
    : ""
  const containerClassName = hasContainerClassName
    ? `fr-container fr-container--large`
    : ""
  const alternateBackground = alternate_background_color
    ? " alternate-background-color"
    : ""

  const buttonParserClassName =
    blockType === "fifty-fifty-grid" ||
    blockType === "one-third-two-thirds-grid"
      ? "content-block-buttons content-block-buttons--start"
      : "content-block-buttons"

  const hasGridLayout =
    blockType === "fifty-fifty-grid" ||
    blockType === "one-third-two-thirds-grid"

  const contentWithoutHeadingClassNameModifier =
    !heading && hasGridLayout ? `${alternatingClassName}-no-heading` : ""

  return (
    <section
      className={`${blockType} ${alternatingClassName} ${contentWithoutHeadingClassNameModifier} ${alternateBackground} ${contentBlockPaddingClassName} u-clearfix`}
    >
      <div className={containerClassName}>
        {(heading || !isSubHeadingEmpty) && (
          <div className={`${blockType}__title`}>
            {eyebrow_text && <p className="eyebrow-text">{eyebrow_text}</p>}
            {heading && <h2>{heading}</h2>}
            {!isSubHeadingEmpty && <h3>{sub_heading}</h3>}
          </div>
        )}
        {image && (
          <div className={`${blockType}__image`}>
            <Image
              image={{
                ...image,
                width: 652,
              }}
              alt={image.alternativeText ?? ""}
            />
          </div>
        )}
        <div className={`${blockType}__content`}>
          {text && text.length > 0 && !isContentEmpty && (
            <div className="text-content-block__content">
              <Content content={text} styleListsAsGrid={false} />
            </div>
          )}
          {buttons && buttons.length > 0 && (
            <div className={buttonParserClassName}>
              <ButtonParser buttons={buttons} />
            </div>
          )}
        </div>
        {children}
      </div>
    </section>
  )
}

export const strapiQuery = graphql`
  fragment StrapiContentBlockFragment on STRAPI__COMPONENT_CONTENT_BLOCK_CONTENT_BLOCK {
    strapi_component
    id
    alternate_background_color
    content_block_type
    eyebrow_text
    heading
    style_lists_as_grid
    sub_heading
    svg_icon
    buttons {
      ...StrapiButtonFragment
    }
    image {
      ...StrapiImageFragment
    }
    reusable_button_group {
      ...StrapiReusableButtonFragment
    }
    text {
      data {
        text
      }
    }
  }
`
